import { useEffect, useState } from "react";
import { X, Gift, Check, Copy, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const SEEN_KEY = "drelo_welcome_seen";

export default function WelcomePopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(SEEN_KEY)) return;
    const timer = setTimeout(() => setOpen(true), 6000);
    const onLeave = (e) => {
      if (e.clientY <= 0 && !localStorage.getItem(SEEN_KEY)) setOpen(true);
    };
    document.addEventListener("mouseleave", onLeave);
    return () => { clearTimeout(timer); document.removeEventListener("mouseleave", onLeave); };
  }, []);

  const close = () => { localStorage.setItem(SEEN_KEY, "1"); setOpen(false); };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      localStorage.setItem(SEEN_KEY, "1");
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText("BIENVENIDA10");
    setCopied(true);
    toast.success("¡Cupón copiado! Úsalo en el carrito 🛒");
    setTimeout(() => setCopied(false), 1500);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 grid place-items-center p-4 animate-rise" data-testid="welcome-popup">
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden relative shadow-2xl">
        <button onClick={close} className="absolute top-3 right-3 z-10 bg-white/80 rounded-full p-1" data-testid="welcome-close">
          <X className="w-5 h-5" />
        </button>

        <div className="bg-gradient-to-br from-drelo-primary to-drelo-deal text-white p-7 text-center relative overflow-hidden">
          <Sparkles className="absolute -right-2 -top-2 w-24 h-24 opacity-20" />
          <Gift className="w-12 h-12 mx-auto mb-2" />
          <div className="font-display font-black text-4xl tracking-tighter uppercase leading-none">-10% OFF</div>
          <p className="text-white/90 text-sm mt-2 font-semibold">en tu primer pedido</p>
        </div>

        {!done ? (
          <form onSubmit={submit} className="p-6 text-center">
            <h2 className="font-display font-black text-xl uppercase">¡Bienvenido a Drelo! 🎉</h2>
            <p className="text-drelo-muted text-sm mt-1.5">Suscríbete y desbloquea <b className="text-drelo-ink">10% de descuento</b> + ofertas exclusivas antes que nadie.</p>
            <input
              required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="Tu mejor email"
              className="w-full border rounded-xl px-4 py-3 mt-4 text-sm outline-none focus:border-drelo-primary text-center"
              data-testid="welcome-email"
            />
            <button disabled={loading} className="w-full bg-drelo-primary text-white py-3.5 rounded-xl font-black uppercase tracking-wide mt-3 hover:bg-drelo-hover active:scale-[0.98] transition-all disabled:opacity-60" data-testid="welcome-submit">
              {loading ? "Activando..." : "Quiero mi 10%"}
            </button>
            <button type="button" onClick={close} className="text-xs text-drelo-muted mt-3 hover:underline" data-testid="welcome-decline">No gracias, no soy de los que aprovechan las oportunidades.</button>
          </form>
        ) : (
          <div className="p-6 text-center" data-testid="welcome-success">
            <div className="w-14 h-14 mx-auto rounded-full bg-green-100 grid place-items-center"><Check className="w-8 h-8 text-drelo-mint" /></div>
            <h2 className="font-display font-black text-xl uppercase mt-3">¡Tu cupón está listo!</h2>
            <p className="text-drelo-muted text-sm mt-1">Aplícalo en el carrito al pagar.</p>
            <button onClick={copyCode} className="mt-4 w-full border-2 border-dashed border-drelo-primary text-drelo-primary rounded-xl py-3 font-display font-black text-xl tracking-widest flex items-center justify-center gap-2 hover:bg-orange-50" data-testid="welcome-copy-code">
              {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />} BIENVENIDA10
            </button>
            <button onClick={close} className="mt-3 w-full bg-drelo-ink text-white py-3 rounded-xl font-black uppercase">Empezar a comprar</button>
          </div>
        )}
      </div>
    </div>
  );
}
